import { Module } from 'vuex';
import { RootState } from './index';
import { SugestaoCampanhaService } from '@/services/SugestaoCampanhaService';
import { ObterSugestaoCampanhaOutputType } from '@/types/sugestoesCampanhas/ObterSugestaoCampanhaOutputType';

export interface SugestaoRespostaState {
  mostrarCampanha: boolean;
  exibirDataCampanha: boolean;
  exibirPesquisaPorPontuacao: boolean;
  exibirCampoRespostaDoUsuario: boolean;
  campanhaAtiva: ObterSugestaoCampanhaOutputType | null;
  carregando: boolean;
  erro: string | null;
}

const state: SugestaoRespostaState = {
  mostrarCampanha: false,
  exibirDataCampanha: false,
  exibirPesquisaPorPontuacao: false,
  exibirCampoRespostaDoUsuario: false,
  campanhaAtiva: null,
  carregando: false,
  erro: null,
};

const mutations = {
  SET_MOSTRAR_CAMPANHA(state: SugestaoRespostaState, valor: boolean) {
    state.mostrarCampanha = valor;
  },
  SET_EXIBIR_DATA_CAMPANHA(state: SugestaoRespostaState, valor: boolean) {
    state.exibirDataCampanha = valor;
  },
  SET_EXIBIR_PESQUISA_POR_PONTUACAO(state: SugestaoRespostaState, valor: boolean) {
    state.exibirPesquisaPorPontuacao = valor;
  },
  SET_EXIBIR_CAMPO_RESPOSTA_DO_USUARIO(state: SugestaoRespostaState, valor: boolean) {
    state.exibirCampoRespostaDoUsuario = valor;
  },
  SET_CAMPANHA_ATIVA(state: SugestaoRespostaState, campanha: ObterSugestaoCampanhaOutputType | null) {
    state.campanhaAtiva = campanha;
  },
  SET_CARREGANDO(state: SugestaoRespostaState, valor: boolean) {
    state.carregando = valor;
  },
  SET_ERRO(state: SugestaoRespostaState, erro: string | null) {
    state.erro = erro;
  },
  CONFIGURAR_CAMPANHA(state: SugestaoRespostaState, campanha: ObterSugestaoCampanhaOutputType) {
    state.campanhaAtiva = campanha;
    state.mostrarCampanha = true;
    state.exibirDataCampanha = campanha.mostrarDataDaCampanha || false;
    state.exibirPesquisaPorPontuacao = campanha.coletarPontuacao || false;
    state.exibirCampoRespostaDoUsuario = campanha.coletarRespostaUsuario || false;
  },
  RESETAR_CAMPANHA(state: SugestaoRespostaState) {
    state.mostrarCampanha = false;
    state.exibirDataCampanha = false;
    state.exibirPesquisaPorPontuacao = false;
    state.exibirCampoRespostaDoUsuario = false;
    state.campanhaAtiva = null;
    state.erro = null;
  },
};

function encontrarCampanhaAtiva(campanhas: ObterSugestaoCampanhaOutputType[]): ObterSugestaoCampanhaOutputType | null {
  const hoje = new Date();

  return campanhas.find((campanha) => {
    if (!campanha || campanha.campanhaDesativada || campanha.campanhaExpirada) {
      return false;
    }

    try {
      const dataInicio = new Date(campanha.dataInicio);
      const dataFim = new Date(campanha.dataFim);

      return hoje >= dataInicio
        && hoje <= dataFim;
    } catch (error) {
      console.error('Erro ao processar datas da campanha:', error, campanha);
      return false;
    }
  }) || null;
}

const actions = {
  async verificarCampanhaAtiva({ commit }) {
    const sugestaoCampanhaService = new SugestaoCampanhaService();

    try {
      commit('SET_CARREGANDO', true);
      commit('SET_ERRO', null);

      await sugestaoCampanhaService.processarCampanhasExpiradas();
      const campanhas = await sugestaoCampanhaService.obterSugestoesCampanhas();

      if (!Array.isArray(campanhas)) {
        throw new Error('Resposta inválida do servidor');
      }

      const campanhaAtiva = encontrarCampanhaAtiva(campanhas);

      if (campanhaAtiva) {
        commit('CONFIGURAR_CAMPANHA', campanhaAtiva);
      } else {
        commit('RESETAR_CAMPANHA');
      }
    } catch (error) {
      console.error('Erro ao verificar campanha ativa:', error);
      commit('SET_ERRO', error instanceof Error ? error.message : 'Erro desconhecido');
      commit('RESETAR_CAMPANHA');
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },

  atualizarConfiguracoesCampanha({ commit }, configuracoes: {
    mostrarData?: boolean;
    coletarPontuacao?: boolean;
    coletarResposta?: boolean;
  }) {
    if (configuracoes.mostrarData !== undefined) {
      commit('SET_EXIBIR_DATA_CAMPANHA', configuracoes.mostrarData);
    }
    if (configuracoes.coletarPontuacao !== undefined) {
      commit('SET_EXIBIR_PESQUISA_POR_PONTUACAO', configuracoes.coletarPontuacao);
    }
    if (configuracoes.coletarResposta !== undefined) {
      commit('SET_EXIBIR_CAMPO_RESPOSTA_DO_USUARIO', configuracoes.coletarResposta);
    }
  },

  ocultarCampanha({ commit }) {
    commit('SET_MOSTRAR_CAMPANHA', false);
  },

  exibirCampanha({ commit, state }) {
    if (state.campanhaAtiva) {
      commit('SET_MOSTRAR_CAMPANHA', true);
    }
  },

  resetarEstado({ commit }) {
    commit('RESETAR_CAMPANHA');
  },
};

const getters = {
  temCampanhaAtiva: (state: SugestaoRespostaState): boolean => {
    const hoje = new Date();

    if (!state.campanhaAtiva) {
      return false;
    }

    try {
      const dataInicio = new Date(state.campanhaAtiva.dataInicio);
      const dataFim = new Date(state.campanhaAtiva.dataFim);

      return hoje >= dataInicio
        && hoje <= dataFim
        && !state.campanhaAtiva.campanhaDesativada
        && !state.campanhaAtiva.campanhaExpirada;
    } catch (error) {
      console.error('Erro ao verificar validade da campanha:', error);
      return false;
    }
  },
  configuracoesCampanha: (state: SugestaoRespostaState) => ({
    mostrarData: state.exibirDataCampanha,
    coletarPontuacao: state.exibirPesquisaPorPontuacao,
    coletarResposta: state.exibirCampoRespostaDoUsuario,
  }),
  dadosCampanhaAtiva: (state: SugestaoRespostaState): ObterSugestaoCampanhaOutputType | null => state.campanhaAtiva,
  estaCarregando: (state: SugestaoRespostaState): boolean => state.carregando,
  temErro: (state: SugestaoRespostaState): boolean => state.erro !== null,
  mensagemErro: (state: SugestaoRespostaState): string | null => state.erro,
  deveExibirCampanha: (state: SugestaoRespostaState): boolean => state.mostrarCampanha,
};

export const sugestaoResposta: Module<SugestaoRespostaState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
